
import { defineComponent, PropType } from 'vue'

interface IData {
  errorText: string
  maxLength: number | null
  isActive: boolean
  localTooLong: boolean
}

const emailFullRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~](?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

const shouldValidateEmail = (value: string): boolean => {
  const trimmed = value.trim()
  const atIndex = trimmed.indexOf('@')
  if (atIndex === -1) {
    return false
  }
  const domain = trimmed.slice(atIndex + 1)
  return domain.includes('.')
}

export default defineComponent({
  name: 'BaseInput',
  props: {
    placeholder: {
      type: String as PropType<string>,
      required: true,
      default: ''
    },
    value: {
      type: String as PropType<string>,
      required: true,
      default: ''
    },
    rule: {
      type: String as PropType<string>,
      require: false,
      default: ''
    },
    withIcon: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false
    },
    error: {
      type: String as PropType<string>,
      require: false,
      default: ''
    },
    blueBorder: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false
    }
  },
  emits: ['getError', 'input'],
  data (): IData {
    return {
      errorText: '',
      maxLength: null,
      isActive: false,
      localTooLong: false
    }
  },
  watch: {
    value () {
      if (this.rule.includes('email')) {
        const value = this.value || ''
        const trimmed = value.trim()

        // Ошибка, если сразу после точки идёт @
        if (/\.@/.test(trimmed)) {
          this.$emit('getError', true)
          this.errorText = 'E-mail содержит ошибку'
          return
        }

        // Не показываем ошибку, пока пользователь не ввёл хотя бы "@"
        // и точку в доменной части
        if (!trimmed.length || !shouldValidateEmail(trimmed)) {
          // Если уже есть @, лимит локальной части не влияет на сообщение
          const hasAt = trimmed.includes('@')
          const showLocalLimit = this.localTooLong && !hasAt
          this.$emit('getError', showLocalLimit)
          this.errorText = showLocalLimit ? 'Вы достигли макс. количества символов в имени' : ''
          return
        }

        const localPart = trimmed.split('@')[0] || ''
        if (
          !localPart ||
          localPart.length > 64 ||
          /^[.-]/.test(localPart) ||
          /\.$/.test(localPart) ||
          /\.@/.test(trimmed) ||
          localPart.includes('..') ||
          /[[\]<>(),;:\s@]/.test(localPart)
        ) {
          this.$emit('getError', true)
          this.errorText = 'E-mail содержит ошибку'
          return
        }

        const res = emailFullRegex.test(trimmed)
        this.$emit('getError', !res)
        this.errorText = res ? '' : 'E-mail содержит ошибку'
      }
    }
  },

  mounted () {
    if (this.rule.includes('promoCode')) {
      this.maxLength = 25
    }
  },

  methods: {
    ruleEmail () {},
    onInput (event: Event) {
      const target = event.target as HTMLInputElement
      const rawValue = target.value
      let value = rawValue
      const lastChar = rawValue.slice(-1)
      // Если предыдущий символ точка и пользователь снова жмёт точку — отбрасываем только новую точку, не трогая остальную строку
      if (lastChar === '.' && rawValue.length >= 2 && rawValue[rawValue.length - 2] === '.') {
        value = rawValue.slice(0, -1)
      }
      let tooLong = false
      if (this.rule.includes('email')) {
        // Оставляем только допустимые ASCII символы для email
        value = value.replace(/[^a-zA-Z0-9.!#$%&'*+/=?^_`{|}~@-]/g, '')
        // Не позволяем начинать с точки или тире
        value = value.replace(/^[.-]+/, '')
        // Не позволяем начинать с @
        value = value.replace(/^@+/, '')
        // Удаляем пробелы и запрещённые символы из доменной части, если они попали
        value = value.replace(/[<>[\](),;:\s]+/g, '')
        // Разрешаем только один символ '@'
        const atIndex = value.indexOf('@')
        if (atIndex !== -1) {
          // После ввода @ лимит локальной части больше не показываем
          tooLong = false
          const before = value.slice(0, atIndex + 1)
          const after = value.slice(atIndex + 1).replace(/@/g, '')
          value = before + after

          // Ограничиваем локальную часть 64 символами
          const localPart = value.slice(0, atIndex)
          if (localPart.length >= 64) {
            value = localPart.slice(0, 64) + value.slice(atIndex)
            tooLong = true
          }
        } else if (value.length >= 64) {
          // Ограничиваем длину, если пользователь ещё не ввёл '@'
          value = value.slice(0, 64)
          tooLong = true
        }
        target.value = value
      }
      this.localTooLong = tooLong
      this.$emit('input', value)
    }
  }
})
